import { APP_SCROLL_CONTAINER_ID } from "@/constants/layout";
import { isSoleScroller } from "@/hooks/useVisibleViewport";
import { visibleViewportVars } from "@/theme";
import { useEffect, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";

export type IframeAutoResizeMessage = {
  type: "ZU_IFRAME_RESIZE";
  height: number;
  pathname?: string;
};

type IframeAutoResizeOptions = {
  /** Minimum requested iframe height, in CSS pixels. */
  minHeight?: number;
  /** Extra pixels for host borders or rounding. */
  heightPadding?: number;
  /** Override the postMessage target origin. */
  targetOrigin?: string;
  /**
   * Fallback for the child-only bottom scroll room, used only where the shell's
   * custom property is unavailable. The applied value is read from the DOM at
   * measurement time; see `readAppliedBottomInset`.
   */
  visibleBottomInset?: number;
  /** Enable or disable resize messages. Enabled by default. */
  enabled?: boolean;
};

const HEIGHT_CHANGE_THRESHOLD = 2;

/**
 * The bottom scroll room the app shell has actually applied, in CSS pixels.
 * Zero unless the app is the only thing on screen that can scroll.
 *
 * Read from the DOM at measurement time rather than taken from the render that
 * scheduled the measurement. The shell adds this room as padding and this hook
 * has to deduct exactly the same number; sourcing them separately let them fall
 * out of phase during a host transition, because a `ResizeObserver` fires as
 * soon as the padding changes while the effect still closes over the previous
 * value. Each such measurement was wrong by one whole inset, and since the
 * result resizes the frame — which changes the inset — the error fed itself:
 * across three fullscreen cycles the requested height oscillated and then ran
 * away from 1089px to 8425px. Reading `scrollHeight` first forces the pending
 * style recalculation, so the value returned here is the one the measured
 * padding resolved from.
 */
const readAppliedBottomInset = (element: Element, fallback: number): number => {
  const view = element.ownerDocument.defaultView;
  if (!view) return fallback;

  const applied = view
    .getComputedStyle(element.ownerDocument.documentElement)
    .getPropertyValue(visibleViewportVars.scrollRoom);
  const parsed = Number.parseFloat(applied);

  return Number.isFinite(parsed) ? parsed : fallback;
};

const getTargetOrigin = (): string => {
  try {
    if (document.referrer) return new URL(document.referrer).origin;
  } catch {
    // An invalid or unavailable referrer is expected in some local embeds.
  }

  return "*";
};

/**
 * Reports the app's content height to a cooperating iframe host.
 *
 * Loop Hubs already handles `ZU_IFRAME_RESIZE`; this is the cross-origin
 * contract that lets the child ask the host to update the actual `<iframe>`
 * element. The app shell grows naturally, while overlays use visible-viewport
 * corrections when the host clips that requested height.
 */
export const useIframeAutoResize = (
  options: IframeAutoResizeOptions = {},
): void => {
  const location = useLocation();
  const targetOrigin = useMemo(
    () => options.targetOrigin ?? getTargetOrigin(),
    [options.targetOrigin],
  );
  const animationFrameRef = useRef<number | null>(null);
  const lastHeightRef = useRef(-1);

  useEffect(() => {
    if (options.enabled === false || window.parent === window) return;

    /*
     * Keep the permanent root as a fallback for bootstrap and test mounts
     * where the app shell may not exist yet, so this effect does not return
     * permanently before the shell mounts.
     */
    const resizeRoot =
      document.getElementById(APP_SCROLL_CONTAINER_ID) ??
      document.getElementById("root") ??
      document.body ??
      document.documentElement;

    const minHeight = options.minHeight ?? 0;
    const heightPadding = options.heightPadding ?? 0;
    const fallbackBottomInset = options.visibleBottomInset ?? 0;
    let shouldReapplyHostHeight = false;

    // Re-send after navigation even when two routes happen to have equal height.
    lastHeightRef.current = -1;

    const postHeight = (height: number) => {
      const message: IframeAutoResizeMessage = {
        type: "ZU_IFRAME_RESIZE",
        height,
        pathname: location.pathname,
      };

      window.parent.postMessage(message, targetOrigin);
    };

    const measureAndPost = (reapplyHostHeight = false) => {
      shouldReapplyHostHeight ||= reapplyHostHeight;

      // Coalesce bursts without moving the pending frame. Cancelling and
      // rescheduling here can starve the first post while a complex route is
      // continuously mounting or receiving data.
      if (animationFrameRef.current !== null) return;

      animationFrameRef.current = requestAnimationFrame(() => {
        animationFrameRef.current = null;
        const reapplyRequestedHeight = shouldReapplyHostHeight;
        shouldReapplyHostHeight = false;

        const measuredHeight = Math.max(
          resizeRoot.scrollHeight,
          resizeRoot.offsetHeight,
          resizeRoot.clientHeight,
        );
        const naturalContentHeight = Math.max(
          0,
          measuredHeight -
            readAppliedBottomInset(resizeRoot, fallbackBottomInset),
        );

        /*
         * Asking for a frame taller than the window is what forces the app to
         * manufacture its own scroll room: the bottom of that frame is off
         * screen and, with the host covered, nothing can reach it. Asking for
         * the window instead makes the frame exactly the area on screen, so the
         * content overflows the document and scrolls the ordinary way, through
         * a scrollbar that is fully visible and sized to real content.
         *
         * The window rather than the measured band, because the band is clipped
         * by the frame's own height: sizing to it could only ever shrink the
         * app, never grow it back to fill the screen.
         */
        const nextHeight = isSoleScroller(window)
          ? Math.round(window.outerHeight)
          : Math.max(
              minHeight,
              Math.ceil(naturalContentHeight + heightPadding),
            );
        const heightChanged =
          Math.abs(nextHeight - lastHeightRef.current) >
          HEIGHT_CHANGE_THRESHOLD;

        /*
         * A host can end up on a height nobody asked for: its fullscreen control
         * restores the original pixel value, or it sizes the frame on its own.
         * Re-send the last request whenever our viewport disagrees with it,
         * even though the content still measures to the same height.
         *
         * Both directions matter. Only correcting a frame that came back too
         * short leaves an oversized one in place forever, because the content
         * measurement that would notice never changes — and an oversized frame
         * is the visible failure, a page of empty space below the content.
         */
        const hostIgnoredRequestedHeight =
          reapplyRequestedHeight &&
          lastHeightRef.current >= 0 &&
          Math.abs(window.innerHeight - lastHeightRef.current) >
            HEIGHT_CHANGE_THRESHOLD &&
          Math.abs(nextHeight - lastHeightRef.current) <=
            HEIGHT_CHANGE_THRESHOLD;

        if (heightChanged || hostIgnoredRequestedHeight) {
          lastHeightRef.current = nextHeight;
          postHeight(nextHeight);
        }
      });
    };

    let resizeObserver: ResizeObserver | null = null;
    let mutationObserver: MutationObserver | null = null;

    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(() => measureAndPost());
      resizeObserver.observe(resizeRoot);

      // Route roots size naturally inside the app shell. Observing them catches
      // async content changes that alter the requested iframe height.
      const observeRouteRoots = () => {
        for (const child of resizeRoot.children) {
          resizeObserver?.observe(child);
        }
      };

      observeRouteRoots();

      /*
       * Suspense and route transitions can replace the observed root without
       * changing the shell's own border box. Use the observer constructor from
       * the shell's document so this also works in same-origin nested test
       * frames, where DOM constructors can otherwise belong to different realms.
       */
      const MutationObserverConstructor =
        resizeRoot.ownerDocument.defaultView?.MutationObserver;
      if (MutationObserverConstructor) {
        mutationObserver = new MutationObserverConstructor(() => {
          observeRouteRoots();
          measureAndPost();
        });
        mutationObserver.observe(resizeRoot, {
          childList: true,
          characterData: true,
          subtree: true,
        });
      }
    }

    const handleViewportResize = () => measureAndPost(true);

    measureAndPost();
    window.addEventListener("resize", handleViewportResize, { passive: true });
    window.visualViewport?.addEventListener("resize", handleViewportResize, {
      passive: true,
    });

    return () => {
      /*
       * Clearing the ref matters as much as cancelling: it outlives this effect,
       * and a cancelled id left behind makes the next run's coalescing guard
       * treat a frame that will never fire as still pending, which stops all
       * further reporting and leaves the host on a stale height.
       */
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      resizeObserver?.disconnect();
      mutationObserver?.disconnect();
      window.removeEventListener("resize", handleViewportResize);
      window.visualViewport?.removeEventListener(
        "resize",
        handleViewportResize,
      );
    };
  }, [
    location.pathname,
    options.enabled,
    options.heightPadding,
    options.minHeight,
    options.visibleBottomInset,
    targetOrigin,
  ]);
};
